import UrlPattern from 'url-pattern';

/**
 * @class ApiService
 * @abstract
 * @template M
 */
export class ApiService {

    /**
     * @type {Object.<string, string>}
     * @protected
     */
    get headers() {

        return {'Content-Type': 'application/json'};
    }

    /**
     * @type {Object.<string, string>}
     * @protected
     */
    get authHeaders() {

        return this.headers;
    }

    /**
     * @type {string}
     * @protected
     */
    get urlPattern() {

        throw new Error("Abstract getter should be implemented in derived class");
    }

    /**
     * @type {string}
     * @protected
     */
    get endpoint() {

        return '';
    }

    /**
     * @param {Object.<string, *>} attributes
     * @param {...*} params
     * @return {M}
     * @abstract
     * @protected
     */
    newModel(attributes, ...params) {

        throw new Error("Abstract method should be implemented in derived class");
    }

    /**
     *
     * @param {Object} response
     * @return {*}
     * @protected
     */
    extractData(response) {

        return response.data;
    }

    /**
     * @returns {function(response: Object): *}
     * @protected
     */
    getDataExtractor() {

        return (response) => this.extractData(response);
    }

    /**
     * @return {function(response: Object): boolean}
     * @protected
     */
    getStatusExtractor() {

        return (response) => true;
    }

    /**
     * @param {...*} params
     * @return {function(response: Object): M}
     * @protected
     */
    getModeler(...params) {

        return (response) => this.newModel(this.extractData(response), ...params);
    }

    /**
     * @param {...*} params
     * @return {function(response: Object): M[]}
     * @protected
     */
    getMassModeler(...params) {

        return (response) => {

            return this.extractData(response)
                       .map((attributes) => this.newModel(attributes, ...params));
        };
    }

    /**
     * @param {Object} response
     * @return {Object}
     * @protected
     */
    handle401Error(response) {

        response.message = response.message || "Unauthorized";

        return response;
    }

    /**
     * @param {Object} response
     * @return {Object}
     * @protected
     */
    handle422Error(response) {

        response.message = response.message || "Validation failed";

        return response;
    }
    
    /**
     * @param {Object} response
     * @return {Object}
     * @protected
     */
    handle500Error(response) {

        response.message = response.message || "Fatal error";

        return response;
    }

    /**
     * @returns {function(error: Object): Promise.<Object>}
     * @protected
     */
    getErrorHandler() {

        return (error) => {
            
            if (!error.status || error.status === -1) {
                
                error.status = 500;
            }
            
            const specificHandlerName = `handle${error.status}Error`;
            if (typeof this[ specificHandlerName ] === 'function') {

                error = this[ specificHandlerName ](error);
            }

            if (!error.message) {
                error.message = error.status ? `${error.status} - ${error.statusText}` : 'Server error';
            }

            return Promise.reject(error);
        }
    }

    /**
     * @param {*} http
     */
    constructor(http) {

        this.http = http;
    }

    /**
     * @param {Object.<string, *>} params
     * @param {string|null} [pattern=null]
     * @return {string}
     */
    url(params = {}, pattern = null) {

        return this.endpoint + (
            new UrlPattern(pattern || this.urlPattern)
        ).stringify(params);
    }
}


// WEBPACK FOOTER //
// ./src/helpers/api-service.js